import React from 'react'
import { UNSAFE_DataRouterStateContext, UNSAFE_NavigationContext, useLocation, useResolvedPath } from 'react-router'
import { Link, NavLinkProps as BaseNavLinkProps, unstable_useViewTransitionState } from 'react-router-dom'

type NavLinkProps = BaseNavLinkProps & { activeTo?: BaseNavLinkProps['to'] }

export default React.forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  {
    'aria-current': ariaCurrentProp = 'page',
    caseSensitive = false,
    className: classNameProp = '',
    end = false,
    style: styleProp,
    to,
    activeTo,
    unstable_viewTransition,
    children,
    ...rest
  },
  ref
) {
  const path = useResolvedPath(activeTo || to, { relative: rest.relative })
  const location = useLocation()
  const routerState = React.useContext(UNSAFE_DataRouterStateContext)
  const { navigator } = React.useContext(UNSAFE_NavigationContext)
  const isTransitioning =
    routerState != null &&
    // Conditional usage is OK here because the usage of a data router is static
    unstable_useViewTransitionState(path) &&
    unstable_viewTransition === true

  let toPathname = navigator.encodeLocation ? navigator.encodeLocation(path).pathname : path.pathname
  let locationPathname = location.pathname
  let nextLocationPathname =
    routerState && routerState.navigation && routerState.navigation.location
      ? routerState.navigation.location.pathname
      : null

  if (!caseSensitive) {
    locationPathname = locationPathname.toLowerCase()
    nextLocationPathname = nextLocationPathname ? nextLocationPathname.toLowerCase() : null
    toPathname = toPathname.toLowerCase()
  }

  // If the `to` has a trailing slash, look at that exact spot.  Otherwise,
  // we're looking for a slash _after_ what's in `to`.  For example:
  //
  // <NavLink to="/users"> and <NavLink to="/users/">
  // both want to look for a / at index 6 to match URL `/users/matt`
  const endSlashPosition = toPathname !== '/' && toPathname.endsWith('/') ? toPathname.length - 1 : toPathname.length
  const isActive =
    locationPathname === toPathname ||
    (!end && locationPathname.startsWith(toPathname) && locationPathname.charAt(endSlashPosition) === '/')

  const isPending =
    nextLocationPathname != null &&
    (nextLocationPathname === toPathname ||
      (!end && nextLocationPathname.startsWith(toPathname) && nextLocationPathname.charAt(toPathname.length) === '/'))

  const renderProps = {
    isActive,
    isPending,
    isTransitioning,
  }

  const ariaCurrent = isActive ? ariaCurrentProp : undefined

  let className: string | undefined
  if (typeof classNameProp === 'function') {
    className = classNameProp(renderProps)
  } else {
    // If the className prop is not a function, we use a default `active`
    // class for <NavLink />s that are active. In v5 `active` was the default
    // value for `activeClassName`, but we are removing that API and can still
    // use the old default behavior for a cleaner upgrade path and keep the
    // simple styling rules working as they currently do.
    className = [
      classNameProp,
      isActive ? 'active' : null,
      isPending ? 'pending' : null,
      isTransitioning ? 'transitioning' : null,
    ]
      .filter(Boolean)
      .join(' ')
  }

  const style = typeof styleProp === 'function' ? styleProp(renderProps) : styleProp

  return (
    <Link
      {...rest}
      aria-current={ariaCurrent}
      className={className}
      ref={ref}
      style={style}
      to={to}
      unstable_viewTransition={unstable_viewTransition}
    >
      {typeof children === 'function' ? children(renderProps) : children}
    </Link>
  )
})
